import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { Form, Button } from 'react-bootstrap';
import './RulesLayout.scss';
import './EditCarriageRule.scss'; // Import the new SCSS file
import Toggle from 'react-toggle';
import { FaTruck, FaShip } from 'react-icons/fa'; // Import Font Awesome icons

const EditCarriageRule = () => {
  const { id } = useParams();
  const [form, setForm] = useState({
    pre_carriage_flg: 0,
    origin_port_code_list: '',
    destination_port_code_list: '',
    duration_days_to_destination: '',
    depart_frequency: '',
    depart_from_duration: '',
    applies_to_origin_destination: '',
    include_exclude_provider_setting: 'no-limits',
    include_exclude_provider_id_list: '',
    display_vessel_as: 'Truck',
    display_carrier_name: '',
    schedule_fixed_flg: 0
  });

  const [selectedOrigins, setSelectedOrigins] = useState([]);
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [selectedAppliesTo, setSelectedAppliesTo] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [scheduleType, setScheduleType] = useState('fixed');
  const [selectedDays, setSelectedDays] = useState([]); // State for selected days
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchRule();
    fetchProviders();
  }, []);

  const fetchRule = async () => {
    try {
      const response = await axios.get(`/api/carriage-rules/${id}`);
      const rule = response.data;

      setForm(rule);

      setSelectedOrigins(rule.origin_port_code_list ? rule.origin_port_code_list.split(',').map(code => ({ value: code, label: code })) : []);
      setSelectedDestinations(rule.destination_port_code_list ? rule.destination_port_code_list.split(',').map(code => ({ value: code, label: code })) : []);
      setSelectedAppliesTo(rule.applies_to_origin_destination ? rule.applies_to_origin_destination.split(',').map(code => ({ value: code, label: code })) : []);

      const selectedProviderIds = rule.include_exclude_provider_id_list ? rule.include_exclude_provider_id_list.split(',') : [];
      const selectedProvidersData = await Promise.all(selectedProviderIds.map(fetchProviderName));
      setSelectedProviders(selectedProvidersData.filter(Boolean));

      setScheduleType(rule.schedule_fixed_flg === 1 ? 'fixed' : 'advanced');

      // Set selected days from the comma-separated list
      if (rule.depart_frequency) {
        setSelectedDays(rule.depart_frequency.split(',').map(day => ({ label: day, value: day })));
      }
    } catch (error) {
      console.error('Error fetching rule', error);
      setError('Error fetching rule data');
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await axios.get('/api/providers/all');
      setProviders(response.data);
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  const fetchProviderName = async (providerId) => {
    try {
      const response = await axios.get(`/api/provider_name/${providerId}`);
      return { provider_id: providerId, provider_name: response.data.provider_name };
    } catch (error) {
      console.error('Error fetching provider name:', error);
      return null;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: value
    }));
  };

  const handleToggle = (e) => {
    const value = e.target.checked ? 1 : 0;
    setForm((prevForm) => ({
      ...prevForm,
      pre_carriage_flg: value
    }));
  };

  const handleScheduleChange = (e) => {
    const { name, value } = e.target;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: value,
      schedule_fixed_flg: name === 'scheduleType' && value === 'fixed' ? 1 : 0
    }));
    if (name === 'scheduleType') {
      setScheduleType(value);
    }
  };

  const handleProviderChange = (e) => {
    const { value } = e.target;
    if (value === 'no-limits') {
      setSelectedProviders([]);
      setForm((prevForm) => ({
        ...prevForm,
        include_exclude_provider_setting: value,
        include_exclude_provider_id_list: ''
      }));
    } else {
      setForm((prevForm) => ({
        ...prevForm,
        include_exclude_provider_setting: value
      }));
    }
  };

  const handleProviderSelect = (selectedOptions) => {
    setSelectedProviders(selectedOptions);
    setForm((prevForm) => ({
      ...prevForm,
      include_exclude_provider_id_list: selectedOptions.map(option => option.value).join(',')
    }));
  };

  const fetchPortOptions = async (inputValue) => {
    try {
      const response = await axios.get('/api/port_code_extended_autosuggest', { params: { query: inputValue } });
      return response.data.map(port => ({ value: port.port_code_suggestion, label: port.port_code_suggestion }));
    } catch (error) {
      console.error('Error fetching port options:', error);
      return [];
    }
  };

  const handleOriginChange = (selectedOptions) => {
    setSelectedOrigins(selectedOptions);
    setForm((prevForm) => ({
      ...prevForm,
      origin_port_code_list: selectedOptions.map(option => option.value).join(',')
    }));
  };

  const handleDestinationChange = (selectedOptions) => {
    setSelectedDestinations(selectedOptions);
    setForm((prevForm) => ({
      ...prevForm,
      destination_port_code_list: selectedOptions.map(option => option.value).join(',')
    }));
  };

  const handleAppliesToChange = (selectedOptions) => {
    setSelectedAppliesTo(selectedOptions);
    setForm((prevForm) => ({
      ...prevForm,
      applies_to_origin_destination: selectedOptions.map(option => option.value).join(',')
    }));
  };

  const handleDaysChange = (selectedOptions) => {
    setSelectedDays(selectedOptions);
    setForm((prevForm) => ({
      ...prevForm,
      depart_frequency: selectedOptions.map(option => option.value).join(',')
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/api/carriage-rules/${id}`, form);
      navigate('/rules/carriage', { state: { successMessage: 'Carriage rule changes saved successfully' } });
    } catch (error) {
      if (error.response && error.response.status === 409) {
        setError('The carriage rule already exists');
      } else {
        console.error('Error updating carriage rule', error);
      }
    }
  };

  const handleCancel = () => {
    navigate('/rules/carriage');
  };

  const dayOptions = [
    { value: 'Monday', label: 'Monday' },
    { value: 'Tuesday', label: 'Tuesday' },
    { value: 'Wednesday', label: 'Wednesday' },
    { value: 'Thursday', label: 'Thursday' },
    { value: 'Friday', label: 'Friday' },
    { value: 'Saturday', label: 'Saturday' },
    { value: 'Sunday', label: 'Sunday' },
    { value: 'Weekly', label: 'Weekly' }
  ];

  return (
    <div>
      <h2>Edit Carriage Rule</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      <form onSubmit={handleSubmit}>
        <section className="form-section">
          <h3>Leg Mode</h3>
          <div className="toggle-switch">
            <Toggle
              checked={form.pre_carriage_flg === 1}
              icons={false}
              onChange={handleToggle} />
            <span className={form.pre_carriage_flg === 1 ? 'highlight' : ''}>Precarriage</span>
            <span className={form.pre_carriage_flg === 0 ? 'highlight' : ''}>Oncarriage</span>
          </div>
        </section>

        <section className="form-section">
          <h3>Routing</h3>
          <Form.Group>
            <Form.Label>
              {form.pre_carriage_flg === 1 ? 'CFS Origin(s) ' : 'Port(s) of Discharge '}
              {form.pre_carriage_flg === 1 ? <FaTruck /> : <FaShip />}
            </Form.Label>
            <AsyncSelect
              isMulti
              cacheOptions
              defaultOptions
              loadOptions={fetchPortOptions}
              value={selectedOrigins}
              onChange={handleOriginChange}
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>
              {form.pre_carriage_flg === 1 ? 'Port(s) of Loading ' : 'CFS Destination(s) '}
              {form.pre_carriage_flg === 1 ? <FaShip /> : <FaTruck />}
            </Form.Label>
            <AsyncSelect
              isMulti
              cacheOptions
              defaultOptions
              loadOptions={fetchPortOptions}
              value={selectedDestinations}
              onChange={handleDestinationChange}
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
            />
          </Form.Group>
        </section>

        <section className="form-section">
          <h3>Schedule</h3>
          <Form.Check
            type="radio"
            label="Fixed Transit Time Leg"
            name="scheduleType"
            value="fixed"
            checked={scheduleType === 'fixed'}
            onChange={handleScheduleChange}
          />
          <div className={`schedule-transition ${scheduleType === 'fixed' ? 'show' : 'hide'}`}>
            <Form.Group>
              <Form.Label>Transit Time</Form.Label>
              <Form.Control
                type="number"
                name="duration_days_to_destination"
                value={form.duration_days_to_destination}
                onChange={handleScheduleChange}
              />
            </Form.Group>
          </div>
          <Form.Check
            type="radio"
            label="Advanced Schedule"
            name="scheduleType"
            value="advanced"
            checked={scheduleType === 'advanced'}
            onChange={handleScheduleChange}
          />
          <div className={`schedule-transition ${scheduleType === 'advanced' ? 'show' : 'hide'}`}>
            <Form.Group>
              <Form.Label>Day(s) of the week</Form.Label>
              <Select
                isMulti
                options={dayOptions}
                value={selectedDays}
                onChange={handleDaysChange}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Transit Time</Form.Label>
              <Form.Control
                type="number"
                name="duration_days_to_destination"
                value={form.duration_days_to_destination}
                onChange={handleScheduleChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Calendar Unit</Form.Label>
              <Form.Control as="select" name="depart_from_duration" value={form.depart_from_duration} onChange={handleScheduleChange}>
                <option value="Calendar Days">Calendar Days</option>
                <option value="Calendar Weeks">Calendar Weeks</option>
              </Form.Control>
            </Form.Group>
          </div>
        </section>

        <section className="form-section">
          <h3>Include/Exclude Providers</h3>
          <Form.Group>
            <Form.Label>Include/Exclude Providers</Form.Label>
            <Form.Control as="select" name="include_exclude_provider_setting" value={form.include_exclude_provider_setting} onChange={handleProviderChange}>
              <option value="no-limits">No limits</option>
              <option value="include">Include</option>
              <option value="exclude">Exclude</option>
            </Form.Control>
          </Form.Group>
          {form.include_exclude_provider_setting !== 'no-limits' && (
            <Form.Group>
              <Form.Label>Select Providers</Form.Label>
              <Select
                isMulti
                options={providers.map(provider => ({ value: provider.provider_id, label: provider.provider_name }))}
                value={selectedProviders}
                onChange={handleProviderSelect}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
              />
            </Form.Group>
          )}
        </section>

        <section className="form-section">
          <h3>Display Settings</h3>
          <Form.Group>
            <Form.Label>Display Vessel Name</Form.Label>
            <Form.Control as="select" name="display_vessel_as" value={form.display_vessel_as} onChange={handleChange}>
              <option value="Truck">Truck</option>
              <option value="Rail">Rail</option>
              <option value="Barge">Barge</option>
              <option value="Feeder">Feeder</option>
            </Form.Control>
          </Form.Group>
          <Form.Group>
            <Form.Label>Display Carrier Name</Form.Label>
            <Form.Control
              type="text"
              name="display_carrier_name"
              value={form.display_carrier_name}
              onChange={handleChange}
              required
            />
          </Form.Group>
        </section>

        <Button type="submit">Save</Button>
        <Button variant="secondary" onClick={handleCancel} style={{ marginLeft: '10px' }}>Cancel</Button>
      </form>
    </div>
  );
};

export default EditCarriageRule;
